import { useState, useEffect } from 'react'
import { FiRefreshCw, FiDatabase, FiSearch, FiPackage } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { useAuth } from '../App'

const fmtNum = (v) => (v == null || isNaN(v)) ? '0' : Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 2 })

export default function Stok() {
  const { selectedCompanyId } = useAuth()
  const [depolar, setDepolar] = useState([])
  const [depo, setDepo] = useState(null)
  const [urunler, setUrunler] = useState([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingUrun, setLoadingUrun] = useState(false)

  useEffect(() => { loadDepolar() }, [selectedCompanyId])

  const loadDepolar = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/albero/stok')
      setDepolar(data.depolar || [])
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Depolar yüklenemedi')
    }
    setLoading(false)
  }

  const selectDepo = async (d, search) => {
    setDepo(d)
    setLoadingUrun(true)
    try {
      const { data } = await api.get('/albero/stok', { params: { depo: d.DEPO_KOD, q: search || undefined } })
      setUrunler(data.urunler || [])
    } catch (e) {
      toast.error('Ürünler yüklenemedi')
    }
    setLoadingUrun(false)
  }

  return (
    <div className="p-4 max-w-[1400px] mx-auto">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-4 mb-4 text-white flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-lg font-bold flex items-center gap-2"><FiDatabase size={18} /> Stok — Depo Bazlı</h1>
        <button onClick={loadDepolar} disabled={loading}
          className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded text-xs">
          <FiRefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Yenile
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Depo listesi */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 overflow-hidden">
          <div className="px-3 py-2 bg-slate-700 text-white text-sm font-medium">Depolar ({depolar.length})</div>
          <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
            {depolar.map((d, i) => (
              <button key={i} onClick={() => selectDepo(d, '')}
                className={`w-full text-left px-3 py-2.5 border-b dark:border-gray-700/50 flex items-center justify-between gap-2 ${depo?.DEPO_KOD === d.DEPO_KOD ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}>
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{d.DEPO_AD || d.DEPO_KOD}</div>
                  <div className="text-[10px] text-gray-400">{d.CESIT} çeşit</div>
                </div>
                <div className="text-sm font-bold text-blue-700 dark:text-blue-400 whitespace-nowrap">{fmtNum(d.STOK)}</div>
              </button>
            ))}
            {!loading && depolar.length === 0 && <div className="text-center text-gray-400 py-10 text-sm">Depo yok</div>}
          </div>
        </div>

        {/* Ürünler */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 overflow-hidden">
          <div className="px-3 py-2 bg-slate-700 text-white flex items-center gap-2">
            <FiPackage size={14} />
            <span className="text-sm font-medium flex-1">{depo ? (depo.DEPO_AD || depo.DEPO_KOD) : 'Depo seçin'}</span>
            {depo && (
              <div className="relative">
                <FiSearch size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/50" />
                <input value={q} onChange={e => setQ(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && selectDepo(depo, q)}
                  placeholder="Ürün ara + Enter"
                  className="pl-6 pr-2 py-1 text-[11px] bg-white/10 border border-white/20 rounded text-white placeholder-white/50 outline-none w-40" />
              </div>
            )}
          </div>
          <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
            {!depo ? (
              <div className="text-center text-gray-400 py-20 text-sm">Soldan bir depo seçin</div>
            ) : loadingUrun ? (
              <div className="text-center text-gray-400 py-20 text-sm">Yükleniyor...</div>
            ) : urunler.length === 0 ? (
              <div className="text-center text-gray-400 py-20 text-sm">Bu depoda stok bulunamadı</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 text-[10px] uppercase">
                  <tr>
                    <th className="text-left px-2 py-1.5">Kod</th>
                    <th className="text-left px-2 py-1.5">Ürün</th>
                    <th className="text-left px-2 py-1.5">Grup</th>
                    <th className="text-right px-2 py-1.5">Stok</th>
                    <th className="text-left px-2 py-1.5 pl-1">Br.</th>
                  </tr>
                </thead>
                <tbody>
                  {urunler.map((u, i) => (
                    <tr key={i} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{u.KOD}</td>
                      <td className="px-2 py-1.5">{u.URUN}</td>
                      <td className="px-2 py-1.5 text-gray-500">{u.GRUP}</td>
                      <td className={`text-right px-2 py-1.5 font-medium ${(u.STOK || 0) < 0 ? 'text-red-600' : 'text-gray-800 dark:text-gray-100'}`}>{fmtNum(u.STOK)}</td>
                      <td className="px-2 py-1.5 pl-1 text-gray-400">{u.BIRIM}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
